/**
 * Staff account management — the wire shapes for the admin-only surface that creates and manages
 * the very accounts `auth.ts` authenticates.
 *
 * This is deliberately a separate module from `auth.ts`. `auth.ts` describes a *session*: who is
 * logged in and what a login submits. This describes the *administration* of accounts, which is a
 * different permission (admin only, not "any recognised role") and a different lifecycle. Keeping
 * them apart means the login path never imports a create-account schema, and a reader of `auth.ts`
 * is not handed the whole of user management to understand a session.
 *
 * What every shape here has in common: `password_hash` is nowhere in it. A staff row on the wire is
 * `sessionUserSchema` — id, nombre, email, rol — the same four fields the dashboard already renders
 * for the logged-in user, and the same structural guarantee (`auth/routes.ts` parses every response
 * through its contract, so a projection that leaked the column would fail its own parse and become a
 * 500 rather than a hash on the wire).
 */
import { z } from 'zod';
import { sessionUserSchema, staffRoleSchema } from './auth.js';
/**
 * The floor a created or reset password must clear. Twelve, the same number `set-password.cli.ts`
 * has always imposed, now in `@apuestapr/contracts` so the CLI, the create route, and the reset
 * route share one truth instead of three copies that drift.
 *
 * `loginRequestSchema` still accepts `.min(1)` on purpose — it describes what may be *submitted* to
 * a login, and refusing a short password there would only tell an attacker that the account they are
 * guessing has a longer one. The policy belongs here, to what *creates* the credential.
 */
export const MIN_PASSWORD_LENGTH = 12;
/**
 * The password half of a create or a reset, in one place so both use the identical rule.
 *
 * Bounded at 200 for the reason `loginRequestSchema` gives: bcrypt reads only the first 72 bytes,
 * so anything past that is neither hashed nor a defence, and the bound stops a megabyte of text
 * from being handed to a deliberately slow function. The `.min` is the policy above, not login's
 * `.min(1)`.
 */
const staffPasswordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Usa al menos ${String(MIN_PASSWORD_LENGTH)} caracteres.`)
    .max(200);
/**
 * A staff row as an admin sees it in the list. The same four fields as the logged-in user, because
 * managing an account needs exactly what identifying one needs, and no more — never the hash, never
 * `ultimo_acceso` or `fecha_creacion`, which no screen reads today and which a later task can add to
 * this schema when one does.
 */
export const staffMemberSchema = sessionUserSchema;
/** The list `GET /api/admin/staff` returns, ordered by the store. */
export const staffListSchema = z.array(staffMemberSchema);
/**
 * Creating an account. The email is normalised exactly as a login normalises it — trimmed,
 * lowercased, piped through `z.email()` — so the unique, case-sensitive column cannot end up holding
 * two spellings of one person, and so the address an admin types matches the address that logs in.
 *
 * `rol` is the enum, not free text: an account created here is always one of the three the API
 * recognises, which is the difference between this route and a hand-typed `INSERT` that produces an
 * account logging in to a 403.
 */
export const createStaffRequestSchema = z.object({
    nombre: z.string().trim().min(1, 'El nombre es obligatorio.').max(200),
    email: z.string().trim().toLowerCase().pipe(z.email()),
    rol: staffRoleSchema,
    password: staffPasswordSchema,
});
/**
 * Changing an account's role. The only field, because a role change is the one edit worth its own
 * route: the name and email are identity an admin rarely touches, and a password is set through the
 * reset route, never read back and re-sent.
 */
export const updateStaffRoleRequestSchema = z.object({
    rol: staffRoleSchema,
});
/**
 * Resetting an account's password. One field, and the response is `staffMemberSchema` rather than
 * anything about the password — the caller set it, so echoing it back is a hash-adjacent value on
 * the wire for no reason. The account's live sessions are the route's concern, not this schema's.
 */
export const resetStaffPasswordRequestSchema = z.object({
    password: staffPasswordSchema,
});
//# sourceMappingURL=staff.js.map